"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Cal, { getCalApi } from "@calcom/embed-react";
import {
  MessageCircle,
  ShoppingBag,
  Users,
  Clock,
  CalendarClock,
  Link2,
  Store,
  GraduationCap,
  Stethoscope,
  BriefcaseBusiness,
  Building2,
  Sparkles,
  ArrowRight,
  Play,
  CheckCircle2,
  Check,
} from "lucide-react";
import logo from "../logo_shopkabot.svg";
import ShopKaBotExplainerSection from "./Homepage/Whatisit";
import SolutionsLikeSection from "./Homepage/Solutions";
import FeaturesSectionLikeImage from "./Homepage/Features";
import LandingNavbar from "./LandingNavbar";
import { reportAdsConversion } from "../utilities/googleAds.utility";
import { useNavigate } from "react-router-dom";

/* ---------------- CONFIG ---------------- */

const PRODUCT_NAME = "ShopKaBot";

const NAV_LINKS = [
  { label: "What it is", href: "#product" },
  { label: "How it helps", href: "#features" },
  { label: "Use cases", href: "#use-cases" },
  { label: "Pricing", href: "#pricing" }, // ✅ added
  { label: "FAQ", href: "#faq" },
];

const WHATSAPP_NUMBER = "919768260471"; // TODO: replace
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  `Hey, I want to know more about ShopKaBot ${PRODUCT_NAME}.`,
)}`;

const DEMO_VIDEO_URL = "https://www.youtube.com/embed/dQw4w9WgXcQ";

/* ---------------- TYPES ---------------- */

type ChatMessage = {
  id: string;
  sender: "user" | "bot" | "system";
  type: "text" | "product" | "payment" | "booking" | "status";
  text?: string;
};

/* ---------------- PRICING ---------------- */
type FeatureItem = {
  text: string;
  included: boolean;
  highlight?: boolean; // optional
};

type PricingSection = {
  title: string;
  items: FeatureItem[];
};

type PricingPlan = {
  id: string;
  name: string;
  badge?: "Popular";
  price: string; // "₹2,499" | "Contact"
  period: string; // "/month" | "for pricing"
  sections: PricingSection[];
  ctaVariant?: "primary" | "secondary";
};

const PRICING_PLANS: PricingPlan[] = [
  {
    id: "broadcast",
    name: "BROADCAST ONLY",
    price: "₹1,229",
    period: "/month",
    sections: [
      {
        title: "FEATURES",
        items: [
          { text: "WhatsApp broadcast message", included: true },
          { text: "Get leads automation", included: true },
          { text: "WhatsApp API", included: true, highlight: true },
          { text: "Custom Flows", included: false },
          { text: "Solves FAQs", included: false },
          { text: "Business Intelligence", included: false },
          { text: "Security for protection of data", included: false },
          { text: "Shopify / Ecommerce integration", included: false },
          {
            text: "Custom WhatsApp flows for your enterprise",
            included: false,
          },
          { text: "ShopKaBot AI Integration + ₹2,500", included: false },
        ],
      },
      {
        title: "TRIAL",
        items: [{ text: "3-day free trial included", included: true }],
      },
    ],
    ctaVariant: "secondary",
  },

  {
    id: "ai_small",
    name: "FOR SMALL BUSINESSES",
    badge: "Popular",
    price: "₹3,499",
    period: "/month",
    sections: [
      {
        title: "INCLUDES",
        items: [
          { text: "Custom Automations", included: true, highlight: true },
          { text: "Solves FAQs", included: true },
          { text: "WhatsApp Bulk Broadcast", included: true },
          { text: "Business Intelligence", included: true },
          { text: "Security for protection of data", included: true },
          { text: "Business related personal features", included: true },
          { text: "ShopKaBot AI Integration + ₹2,500", included: true },
          { text: "Shopify / Ecommerce integration", included: false },
          { text: "Custom flow for business", included: false },
          {
            text: "Custom WhatsApp flows for your enterprise",
            included: false,
          },
        ],
      },
      {
        title: "CAPACITY",
        items: [{ text: "50–100 conversations per day", included: true }],
      },
      {
        title: "TRIAL",
        items: [{ text: "3-day free trial included", included: true }],
      },
    ],
    ctaVariant: "primary",
  },

  {
    id: "ai_high",
    name: "FOR HIGH VOLUME BUSINESSES",
    price: "₹4,999",
    period: "/month",
    sections: [
      {
        title: "INCLUDES",
        items: [
          { text: "Custom Automations", included: true, highlight: true },
          { text: "Shopify / Ecommerce integration", included: true },
          { text: "Solves FAQs", included: true },
          { text: "WhatsApp Bulk Broadcast", included: true },
          { text: "Business Intelligence", included: true },
          { text: "Security for protection of data", included: true },
          { text: "Custom flow for business", included: true },
          { text: "Business related personal features", included: true },
          { text: "ShopKaBot AI Integration + ₹2,500", included: true },
          {
            text: "Custom WhatsApp flows for your enterprise",
            included: false,
          },
        ],
      },
      {
        title: "CAPACITY",
        items: [{ text: "100–500 conversations per day", included: true }],
      },
      {
        title: "TRIAL",
        items: [{ text: "3-day free trial included", included: true }],
      },
    ],
    ctaVariant: "secondary",
  },

  {
    id: "enterprise",
    name: "ENTERPRISE",
    price: "Contact",
    period: "for pricing",
    sections: [
      {
        title: "INCLUDES",
        items: [
          {
            text: "Security for protection of data",
            included: true,
            highlight: true,
          },
          { text: "AI for business solution", included: true },
          { text: "Custom WhatsApp flows for your enterprise", included: true },
          { text: "Business related personal features", included: true },
          { text: "ShopKaBot AI Integration + ₹2,500", included: true },
          { text: "Custom Automations", included: true },
          { text: "Solves FAQs", included: true },
          { text: "WhatsApp Bulk Broadcast", included: true },
          { text: "Business Intelligence", included: true },
          { text: "Shopify / Ecommerce integration", included: true },
          { text: "Custom flow for business", included: true },
        ],
      },
      {
        title: "CAPACITY",
        items: [
          { text: "More than 500 conversations per day", included: true },
        ],
      },
      {
        title: "TRIAL",
        items: [{ text: "3-day free trial available", included: true }],
      },
    ],
    ctaVariant: "secondary",
  },
];

/* ---------------- CHAT SCRIPTS ---------------- */

const CHAT_SCRIPT: ChatMessage[] = [
  {
    id: "1",
    sender: "user",
    type: "text",
    text: "Hi, can you show me your best-selling hair serums under ₹800?",
  },
  {
    id: "2",
    sender: "bot",
    type: "product",
    text: "Sure! Here are top picks under ₹800 👇",
  },
  {
    id: "3",
    sender: "user",
    type: "text",
    text: "Nice! I’ll take the Walnut Shine Serum. Can I pay online?",
  },
  {
    id: "4",
    sender: "bot",
    type: "payment",
    text: "Absolutely. Here’s a secure payment link for your order:",
  },
  {
    id: "5",
    sender: "user",
    type: "text",
    text: "Also, I want help setting up booking automation for hair care.",
  },
  {
    id: "6",
    sender: "bot",
    type: "booking",
    text: "Done. Pick a 20-min slot for a consult:",
  },
  {
    id: "7",
    sender: "user",
    type: "text",
    text: "Book me for Sat, 18 Jan, 5:00 PM.",
  },
  {
    id: "8",
    sender: "bot",
    type: "status",
    text: "✅ Booked for Sat, 18 Jan • 5:00 PM. Confirmation sent on WhatsApp.",
  },
];

const SALES_CHAT_SCRIPT: ChatMessage[] = [
  {
    id: "s1",
    sender: "user",
    type: "text",
    text: "Hey, I saw your Glow Combo on Instagram. What’s included?",
  },
  {
    id: "s2",
    sender: "bot",
    type: "text",
    text: "Glow Combo = Face Wash + Serum + Sunscreen for ₹1,499.",
  },
  {
    id: "s3",
    sender: "user",
    type: "text",
    text: "My skin is oily. Is this okay or suggest something else?",
  },
  {
    id: "s4",
    sender: "bot",
    type: "text",
    text: "For oily skin, most customers prefer the Tea Tree Kit. Switch your cart to that?",
  },
  {
    id: "s5",
    sender: "user",
    type: "text",
    text: "Yes, switch it and send payment link.",
  },
  {
    id: "s6",
    sender: "bot",
    type: "status",
    text: "✅ Updated. Here’s your secure payment link.",
  },
];

/* ---------------- UI PRIMITIVES ---------------- */

function Container({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-6xl px-4">{children}</div>;
}

function Section({
  id,
  eyebrow,
  title,
  desc,
  children,
  className = "",
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  desc?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`border-b border-slate-100 ${className}`}>
      <Container>
        <div className="py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            {eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                {eyebrow}
              </p>
            )}
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {title}
            </h2>
            {desc && (
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600 md:text-[16px]">
                {desc}
              </p>
            )}
          </div>
          <div className="mt-10">{children}</div>
        </div>
      </Container>
    </section>
  );
}

function PrimaryButton({
  children,
  onClick,
  href,
  target,
  rel,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
}) {
  const cls = [
    "inline-flex items-center justify-center gap-2",
    "h-14 px-8 text-base",
    "rounded-full",
    "bg-emerald-600 text-white",
    "font-semibold",
    "shadow-sm transition-all duration-200",
    "hover:bg-emerald-700 hover:shadow-md hover:-translate-y-[1px]",
    "active:translate-y-0 active:shadow-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    "disabled:opacity-60 disabled:pointer-events-none",
    className,
  ].join(" ");

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={cls}
      >
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
  href,
  target,
  rel,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
}) {
  const cls = [
    "inline-flex items-center justify-center gap-2",
    "h-14 px-8 text-base",
    "rounded-full",
    "border border-slate-200 bg-white text-slate-800",
    "font-semibold transition-all duration-200",
    "hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-sm",
    "active:bg-emerald-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    "disabled:opacity-60 disabled:pointer-events-none",
    className,
  ].join(" ");

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={cls}
      >
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

function MetricPill({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-slate-100 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      {label}
    </span>
  );
}

/* ---------------- PRICING UI (MINIMAL, TRUSTY) ---------------- */

function PricingPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
      {children}
    </span>
  );
}

function PricingCTA({
  variant,
  onClick,
}: {
  variant: "primary" | "secondary";
  onClick: () => void;
}) {
  if (variant === "primary") {
    return (
      <button
        onClick={onClick}
        className="block w-full py-3 rounded-lg font-semibold mt-4 text-center bg-emerald-600 text-white hover:bg-emerald-700 transition"
      >
        Start 3-day trial
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      className="block w-full py-3 rounded-lg font-semibold mt-4 text-center bg-slate-100 text-black hover:bg-slate-200 transition"
    >
      Start 3-day trial
    </button>
  );
}

function PricingBullet({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-4 h-4 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check className="w-3 h-3 text-white" />
      </div>
      <span className="text-xs text-slate-700">{text}</span>
    </div>
  );
}
const PricingFeatureList = ({ items }: { items: FeatureItem[] }) => {
  const sorted = [...items].sort(
    (a, b) => Number(b.included) - Number(a.included),
  );

  return (
    <ul className="mt-2 space-y-2 text-left">
      {sorted.map((it) => {
        const [labelRaw, ...rest] = it.text.split(":");
        const hasPair = rest.length > 0;
        const label = hasPair ? labelRaw.trim() : it.text.trim();
        const value = hasPair ? rest.join(":").trim() : "";

        const labelClass = [
          "min-w-0 flex-1 whitespace-normal break-words", // wrap on new line
          it.included
            ? "font-semibold text-slate-900" // enabled bold
            : "text-slate-400 line-through", // disabled cut
          it.highlight && it.included ? "text-slate-950" : "",
        ].join(" ");

        const valueClass = [
          "shrink-0 text-left whitespace-normal break-words", // keep left aligned + wrap if needed
          it.included
            ? "font-semibold text-slate-900"
            : "text-slate-400 line-through",
        ].join(" ");

        return (
          <li
            key={it.text}
            className={["flex items-start gap-3", "text-[13px] leading-5"].join(
              " ",
            )}
          >
            {/* If no pair: single line block that wraps */}
            {!hasPair ? (
              <span className={labelClass}>{label}</span>
            ) : (
              // If pair: keep label and value in a 2-col grid so alignment is consistent
              <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 w-full">
                <span className={labelClass}>{label}</span>
                <span className={valueClass}>{value}</span>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

function PricingSection({ onStart }: { onStart: () => void }) {
  return (
    <section id="pricing" className="border-b border-slate-100 bg-[#f8f6f4]">
      <Container>
        <div className="py-12 md:py-16">
          {/* header like screenshot */}
          <div className="text-center mb-10 md:mb-12">
            <p className="text-emerald-700 text-sm font-semibold">
              Pricing Plans
            </p>
            <h2 className="mt-2 text-4xl md:text-5xl font-serif font-bold text-slate-900">
              3-day Free Trial,
            </h2>
            <p className="mt-3 text-sm text-slate-500">
              No Contracts &nbsp;•&nbsp; Cancel anytime
            </p>
          </div>

          {/* wrapper block */}

          <div className="grid grid-cols-1 md:grid-cols-1 xl:grid-cols-4 gap-6">
            {PRICING_PLANS.map((plan) => {
              const isPopular = plan.badge === "Popular";

              return (
                <div
                  key={plan.id}
                  className={[
                    "relative bg-white rounded-2xl border p-6",
                    "shadow-sm hover:shadow-md transition-shadow",
                    "flex flex-col", // so CTA can stick to bottom
                    isPopular
                      ? "border-slate-900 ring-1 ring-slate-900/10"
                      : "border-slate-200",
                  ].join(" ")}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <PricingPill>Popular</PricingPill>
                    </div>
                  )}

                  {/* Header (centered) */}
                  <div className="text-center mb-5">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {plan.name}
                    </h3>

                    {/* Price centered */}
                    <div className="mt-3 flex items-baseline justify-center gap-2 text-slate-900">
                      <span
                        className={
                          plan.price === "Contact"
                            ? "text-2xl font-semibold"
                            : "text-3xl font-bold"
                        }
                      >
                        {plan.price}
                      </span>
                      <span className="text-sm text-slate-500">
                        {plan.period}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    {plan.sections.map((sec) => (
                      <section
                        key={sec.title}
                        className="pt-4 border-t border-slate-100"
                      >
                        <PricingFeatureList items={sec.items} />
                      </section>
                    ))}
                  </div>

                  {/* CTA at bottom */}
                  <div className="mt-auto pt-6">
                    <PricingCTA
                      variant={
                        plan.ctaVariant === "primary" ? "primary" : "secondary"
                      }
                      onClick={onStart}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center text-xs text-slate-500">
            3 days free trial for all plans
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------------- BOOKING SECTION (CAL.COM INLINE) ---------------- */

function BookingSection() {
  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "30min" });
      cal("ui", {
        theme: "light",
        hideEventTypeDetails: true,
        layout: "month_view",
      });
    })();
  }, []);

  return (
    <section id="booking" className="bg-white">
      <Container>
        <div className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl">
            <div className="mb-8 text-center">
              <p className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-200 bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-800">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                Book your demo
              </p>
              <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-4xl">
                See ShopKaBot in action
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-600">
                Pick a time that works for you. We'll walk you through how to
                turn your WhatsApp into a sales powerhouse.
              </p>
            </div>

            <div className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <Cal
                namespace="30min"
                calLink="dulawat-code-pxvwzn/30min"
                style={{ width: "100%", height: "auto" }}
                config={{ layout: "month_view", theme: "light" }}
              />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------------- HERO CHAT CARD ---------------- */

function HeroChatCard() {
  const [visibleCount, setVisibleCount] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [typingSender, setTypingSender] = useState<"user" | "bot" | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms));

    async function run() {
      while (!cancelled) {
        setVisibleCount(1);
        setIsTyping(false);
        setTypingSender(null);

        await sleep(1000);
        if (cancelled) return;

        for (let i = 1; i < CHAT_SCRIPT.length; i++) {
          const nextMsg = CHAT_SCRIPT[i];

          setTypingSender(nextMsg.sender === "user" ? "user" : "bot");
          setIsTyping(true);
          await sleep(650);
          if (cancelled) return;

          setVisibleCount((prev) => Math.min(prev + 1, CHAT_SCRIPT.length));
          setIsTyping(false);
          setTypingSender(null);

          await sleep(950);
          if (cancelled) return;
        }

        await sleep(1700);
        if (cancelled) return;
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (listRef.current)
      listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [visibleCount, isTyping]);

  const visibleMessages = CHAT_SCRIPT.slice(0, visibleCount);

  return (
    <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-100 opacity-70 blur-3xl" />
      <div className="pointer-events-none absolute -left-16 bottom-0 h-28 w-28 rounded-full bg-emerald-50 opacity-80 blur-3xl" />

      <div className="relative z-10 mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600/10 text-[11px] font-semibold text-emerald-700">
            WA
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-900">
              Customer chat
            </p>
            <p className="text-[11px] text-slate-500">
              WhatsApp • Powered by {PRODUCT_NAME}
            </p>
          </div>
        </div>

        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
          Replies instantly
        </span>
      </div>

      <div className="relative z-10 mb-3 flex flex-wrap gap-2">
        <MetricPill label="Replies 24×7" />
        <MetricPill label="Sends payment link" />
        <MetricPill label="Books appointments" />
      </div>

      <div
        ref={listRef}
        className="scrollbar-hide relative z-10 h-80 space-y-3 overflow-y-auto pr-1 text-xs md:h-96"
      >
        {visibleMessages.map((msg) => {
          const isUser = msg.sender === "user";
          const isBot = msg.sender === "bot";
          const alignClass = isUser ? "justify-start" : "justify-end";

          return (
            <div key={msg.id} className={`flex ${alignClass}`}>
              <div
                className={[
                  "max-w-[84%] rounded-2xl px-3 py-2",
                  isUser
                    ? "border border-slate-200 bg-white text-slate-800 shadow-sm"
                    : "bg-emerald-600 text-white shadow-sm",
                ].join(" ")}
              >
                {msg.type === "product" && isBot && (
                  <div>
                    <p className="mb-2 text-[11px] text-emerald-50">
                      {msg.text}
                    </p>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between rounded-xl bg-emerald-700/35 px-2 py-1.5 text-[11px]">
                        <div>
                          <p className="font-semibold">Walnut Shine Serum</p>
                          <p className="text-emerald-100">₹699 • 50 ml</p>
                        </div>
                        <span className="rounded-full bg-emerald-500/80 px-2 py-0.5 text-[10px]">
                          Bestseller
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-emerald-700/20 px-2 py-1.5 text-[11px]">
                        <div>
                          <p className="font-semibold">Argan Repair Serum</p>
                          <p className="text-emerald-100">₹749 • 45 ml</p>
                        </div>
                        <span className="text-[10px] text-emerald-100">
                          In stock
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 text-[10px] text-emerald-100">
                      Reply with the product name to add to cart.
                    </p>
                  </div>
                )}

                {msg.type === "payment" && isBot && (
                  <div>
                    <p className="mb-2 text-[11px] text-emerald-50">
                      {msg.text}
                    </p>
                    <div className="rounded-xl bg-white/10 px-3 py-2">
                      <p className="text-[11px]">
                        Order:{" "}
                        <span className="font-semibold">
                          Walnut Shine Serum
                        </span>
                      </p>
                      <p className="text-[11px]">
                        Amount: <span className="font-semibold">₹699</span>
                      </p>
                      <p className="mt-2 rounded-full bg-emerald-200 px-3 py-1.5 text-[10px] font-semibold text-emerald-900">
                        Payment link generated • Pay via UPI / Card
                      </p>
                    </div>
                    <p className="mt-1 text-[10px] text-emerald-100">
                      Customer stays in chat. You get an order instantly.
                    </p>
                  </div>
                )}

                {msg.type === "booking" && isBot && (
                  <div>
                    <p className="mb-2 text-[11px] text-emerald-50">
                      {msg.text}
                    </p>
                    <div className="space-y-1.5">
                      {[
                        "Sat, 18 Jan • 5:00 PM",
                        "Sun, 19 Jan • 11:30 AM",
                        "Mon, 20 Jan • 7:00 PM",
                      ].map((slot) => (
                        <div
                          key={slot}
                          className="flex items-center justify-between rounded-xl bg-emerald-700/35 px-2 py-1.5 text-[11px]"
                        >
                          <p className="font-medium">{slot}</p>
                          <span className="text-[10px] text-emerald-100">
                            Available
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-1 text-[10px] text-emerald-100">
                      Reply with a slot to confirm.
                    </p>
                  </div>
                )}

                {msg.type === "status" && (
                  <p className="text-[11px] leading-snug">{msg.text}</p>
                )}
                {msg.type === "text" && (
                  <p className="text-[11px] leading-snug">{msg.text}</p>
                )}
              </div>
            </div>
          );
        })}

        {isTyping && typingSender && (
          <div
            className={`flex ${
              typingSender === "user" ? "justify-start" : "justify-end"
            }`}
          >
            <div
              className={[
                "max-w-[50%] rounded-2xl px-3 py-2",
                typingSender === "user"
                  ? "border border-slate-200 bg-white text-slate-800 shadow-sm"
                  : "bg-emerald-600 text-white shadow-sm",
              ].join(" ")}
            >
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300 [animation-delay:0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300 [animation-delay:0.3s]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="relative z-10 mt-4 text-[11px] leading-relaxed text-slate-500">
        The point: fewer missed messages → faster replies → more conversions.
      </p>
    </div>
  );
}

/* ---------------- PAGE ---------------- */

export default function HomePage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const navigate = useNavigate();

  const goToBuy = () => navigate("/buy");

  // Pricing-section scroll helper retained for easy re-enablement.
  // const scrollToId = (id: string) => {
  //   const el = document.getElementById(id);
  //   if (!el) return;
  //   el.scrollIntoView({ behavior: "smooth", block: "start" });
  // };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* ✅ NAVBAR (hook pricing + demo) */}
      <LandingNavbar />

      <main>
        {/* HERO */}
        <section className="overflow-hidden border-b border-slate-100 bg-gradient-to-b from-emerald-50/70 via-white to-white">
          <Container>
            <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 lg:gap-y-20 my-5">
              <div className="relative z-10 mx-auto max-w-2xl lg:col-span-7 lg:max-w-none lg:pt-6 xl:col-span-6">
                <p className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-200 bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-800">
                  <span className="h-1.5 w-1.5 text-4xl rounded-full bg-emerald-600" />
                  WhatsApp chatbot + sales on autopilot
                </p>

                <h1 className="mt-6 text-4xl font-semibold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                  Faster replies.
                  <br />
                  <span className="text-emerald-700">More sales.</span>
                  <br />
                </h1>

                <h2>
                  <span className="text-emerald-700 text-2xl">
                    Turns your Whatsapp into AI Assistant Chatbot.
                  </span>
                </h2>

                <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-slate-700 md:text-[17px]">
                  {PRODUCT_NAME} uses the latest AI Agents to convert your
                  WhatsApp into an AI sales assistant — automates replies,
                  recommends products, sends payment links, and books
                  appointments.
                </p>
                <br />
                <span className="text-emerald-700 text-2xl">
                  7 Days Free Trail.
                </span>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  {/* <PrimaryButton
                    href="/contact-us"
                    className="w-full justify-center rounded-xl px-6 py-3 text-base sm:w-auto sm:text-[15px] lg:px-7 lg:py-3.5 lg:text-[16px]"
                    onClick={(e) => {
                      e.preventDefault();
                      goToDemo();
                    }}
                  >
                    Fill lead form <ArrowRight className="h-4 w-4" />
                  </PrimaryButton> */}
                  <PrimaryButton
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full justify-center rounded-xl px-6 py-3 text-base sm:w-auto sm:text-[15px] lg:px-7 lg:py-3.5 lg:text-[16px]"
                    onClick={(e) => {
                      e.preventDefault();
                      reportAdsConversion({
                        onDone: () =>
                          window.open(WHATSAPP_LINK, "_blank", "noreferrer"),
                      });
                    }}
                  >
                    WhatsApp our founders <MessageCircle className="h-4 w-4" />
                  </PrimaryButton>

                  <SecondaryButton
                    href="/buy"
                    className="w-full justify-center rounded-xl px-6 py-3 text-base sm:w-auto sm:text-[15px] lg:px-7 lg:py-3.5 lg:text-[16px]"
                    onClick={(e) => {
                      e.preventDefault();
                      goToBuy();
                    }}
                  >
                    Buy ShopKaBot
                  </SecondaryButton>
                </div>
              </div>

              <div className="relative mt-10 sm:mt-20 lg:col-span-5 lg:row-span-2 lg:mt-0 xl:col-span-6">
                <div className="pointer-events-none absolute -right-24 -top-10 h-72 w-72 rounded-full bg-emerald-100/70 blur-3xl" />

                <div className="relative flex justify-center lg:justify-end lg:translate-x-10 xl:translate-x-14">
                  <div className="w-full max-w-[560px]">
                    <HeroChatCard />
                    <p className="mt-3 text-center text-[11px] text-slate-500">
                      Fewer missed messages → faster replies → more conversions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* YOUR SECTIONS */}
        <FeaturesSectionLikeImage />

        <section id="solutions">
          <SolutionsLikeSection />
        </section>
        {/* ✅ PRICING SECTION INSERTED HERE */}
        {/* <PricingSection onStart={goToBuy} /> */}

        {/* BLOG SECTION */}
        <Section
          eyebrow="Resources"
          title="Learn AI Automation"
          desc="Expert guides, strategies, and insights to help you automate your business."
          className="bg-white"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Complete Guide to WhatsApp Business Automation",
                desc: "Learn how to automate your WhatsApp operations and increase efficiency with AI agents.",
                category: "Guides",
              },
              {
                title: "How AI Chatbots Transform Customer Service",
                desc: "Discover how modern AI chatbots revolutionize customer service and improve business outcomes.",
                category: "Industry Insights",
              },
              {
                title: "E-commerce Sales Automation Strategies",
                desc: "Proven strategies for automating e-commerce sales and increasing conversion rates.",
                category: "Strategy",
              },
            ].map((post, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate("/blogs")}
              >
                <span className="text-xs font-semibold text-emerald-700 uppercase">
                  {post.category}
                </span>
                <h3 className="mt-3 text-lg font-semibold text-slate-900 line-clamp-2">
                  {post.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                  {post.desc}
                </p>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => navigate("/blogs")}
                    className="text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                  >
                    Read More →
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <PrimaryButton
              href="/blogs"
              onClick={(e) => {
                e.preventDefault();
                navigate("/blogs");
              }}
            >
              View All Articles
            </PrimaryButton>
          </div>
        </Section>

        {/* TOOLS SECTION */}
        <Section
          eyebrow="Tools"
          title="Free Resources & Tools"
          desc="Access our collection of free tools designed to help you automate faster."
          className="bg-slate-50"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "🧮",
                title: "ROI Calculator",
                desc: "Calculate the potential savings from WhatsApp automation.",
              },
              {
                icon: "📚",
                title: "Setup Guide",
                desc: "Step-by-step guide to deploy your first AI agent.",
              },
              {
                icon: "⚡",
                title: "Workflow Builder",
                desc: "Visual tool to design automation workflows.",
              },
            ].map((tool, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-slate-200 bg-white p-6 text-center hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate("/tools")}
              >
                <div className="text-4xl mb-4">{tool.icon}</div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {tool.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  {tool.desc}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <PrimaryButton
              href="/tools"
              onClick={(e) => {
                e.preventDefault();
                navigate("/tools");
              }}
            >
              Explore Tools
            </PrimaryButton>
          </div>
        </Section>

        {/* ✅ PRICING SECTION INSERTED HERE */}
        {/* <PricingSection onStart={goToBuy} /> */}
        <Section
          id="faq"
          eyebrow="FAQ"
          title="Simple answers."
          desc=""
          className="bg-gradient-to-b from-emerald-50/70 via-white to-slate-50/70"
        >
          <div className="mx-auto max-w-3xl space-y-3">
            {[
              {
                q: `What does ${PRODUCT_NAME} do?`,
                a: "It replies to customers, recommends products/services, collects details, shares payment links, and confirms bookings — automatically.",
              },
              {
                q: "How fast can I go live?",
                a: "Usually within a day for a basic setup. If you have a catalogue + FAQs ready, it’s even faster.",
              },
              {
                q: "Do I need a developer?",
                a: "No. We set up WhatsApp + your flows. You only provide business info, products, and policies.",
              },
              {
                q: "Does it support Hindi / Hinglish?",
                a: "Yes — it can respond in English, Hindi, and Hinglish based on your customers.",
              },
              {
                q: "What if it gives a wrong answer?",
                a: "You can correct it once and it learns the updated response for next time.",
              },
            ].map((item) => (
              <details
                key={item.q}
                className="group rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-slate-900">
                  {item.q}
                  <span className="text-xs font-semibold text-emerald-700 transition group-open:rotate-90">
                    →
                  </span>
                </summary>
                <p className="mt-2 text-[12px] leading-relaxed text-slate-600">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </Section>

        {/* FINAL CTA */}
        <section className="bg-white">
          <Container>
            <div className="py-12 text-center md:py-16">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                Want to stop losing leads to slow replies?
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-600">
                Start with ShopKaBot or WhatsApp us. We’ll show you how automation
                turns chats into orders and bookings without adding headcount.
              </p>

              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <PrimaryButton
                  href="/buy"
                  onClick={(e) => {
                    e.preventDefault();
                    goToBuy();
                  }}
                >
                  Buy ShopKaBot <ArrowRight className="h-4 w-4" />
                </PrimaryButton>

                <SecondaryButton
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    reportAdsConversion({
                      onDone: () =>
                        window.open(WHATSAPP_LINK, "_blank", "noreferrer"),
                    });
                  }}
                >
                  WhatsApp directly
                </SecondaryButton>
              </div>
            </div>
          </Container>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-100 bg-slate-50">
        <Container>
          <div className="flex flex-col gap-3 py-6 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">
                {PRODUCT_NAME}
              </span>
              <span>• Automated WhatsApp that drives sales.</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Disabled routes retained for easy re-enablement.
              <a href="/privacy-policy" className="hover:text-emerald-700">
                Privacy Policy
              </a>
              <a href="/terms-of-service" className="hover:text-emerald-700">
                Terms
              </a>
              */}
              <span>
                © {new Date().getFullYear()} {PRODUCT_NAME} Technologies
              </span>
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
}
